import { Button, IconButton, ToggleGroup } from "@/library"
import { ChevronLeft, ChevronRight } from "@mui/icons-material"
import { Box, ToggleButton, Typography, useMediaQuery, useTheme } from "@mui/material"
import { styled } from "@mui/system"
import { endOfWeek, format, parseISO, startOfWeek } from "date-fns"
import PropTypes from "prop-types"
import React, { useMemo } from "react"
import { VIEW_MODES } from "./constants"

const NavigationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}))

const CalendarControls = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    justifyContent: "center",
  },
}))

const CalendarNavigation = ({
  onNavigateToday,
  onNavigatePrev,
  onNavigateNext,
  currentDate,
  isLoading,
  showViewToggle,
  currentView,
  onViewChange,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const dateObj = useMemo(() => parseISO(currentDate), [currentDate])

  // Format the date title based on the view mode
  const formattedDate = useMemo(() => {
    if (currentView === VIEW_MODES.WEEK) {
      // For week view, show date range (e.g., "March 28 - April 3, 2023")
      const weekStart = startOfWeek(dateObj, { weekStartsOn: 0 })
      const weekEnd = endOfWeek(dateObj, { weekStartsOn: 0 })

      // If same month
      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${format(weekStart, "MMMM d")} - ${format(weekEnd, "d, yyyy")}`
      }

      // If different months
      if (weekStart.getFullYear() === weekEnd.getFullYear()) {
        return `${format(weekStart, "MMMM d")} - ${format(weekEnd, "MMMM d, yyyy")}`
      }

      // If different years
      return `${format(weekStart, "MMMM d, yyyy")} - ${format(weekEnd, "MMMM d, yyyy")}`
    }

    // For month view, just show the month and year
    return format(dateObj, "MMMM yyyy")
  }, [currentView, dateObj])

  const periodLabel = useMemo(() => {
    return currentView === VIEW_MODES.WEEK ? "Week" : "Month"
  }, [currentView])

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      onViewChange(newView)
    }
  }

  return (
    <NavigationContainer
      role="navigation"
      aria-label="Calendar navigation"
      sx={{ display: "flex", alignItems: "flex-end", gap: 1, flexDirection: { xs: "column", sm: "row" } }}
    >
      <Typography variant={isMobile ? "h6" : "h4"} color="rgb(31, 0, 122)">
        {formattedDate}
      </Typography>
      <CalendarControls>
        {showViewToggle && (
          <ToggleGroup value={currentView} exclusive onChange={handleViewChange} aria-label="calendar view">
            <ToggleButton value={VIEW_MODES.WEEK} aria-label="week view">
              Week
            </ToggleButton>
            <ToggleButton value={VIEW_MODES.MONTH} aria-label="month view">
              Month
            </ToggleButton>
          </ToggleGroup>
        )}
        <IconButton
          onClick={onNavigatePrev}
          disabled={isLoading}
          aria-label={`Previous ${periodLabel.toLowerCase()}`}
          size="medium"
        >
          <ChevronLeft />
        </IconButton>
        <Button variant="outlined" size="small" onClick={onNavigateToday} disabled={isLoading} aria-label="Go to today">
          Today
        </Button>
        <IconButton
          onClick={onNavigateNext}
          disabled={isLoading}
          aria-label={`Next ${periodLabel.toLowerCase()}`}
          size="medium"
        >
          <ChevronRight />
        </IconButton>
      </CalendarControls>
    </NavigationContainer>
  )
}

CalendarNavigation.propTypes = {
  onNavigateToday: PropTypes.func.isRequired,
  onNavigatePrev: PropTypes.func.isRequired,
  onNavigateNext: PropTypes.func.isRequired,
  currentDate: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  showViewToggle: PropTypes.bool,
  currentView: PropTypes.oneOf(["week", "month"]),
  onViewChange: PropTypes.func,
}

CalendarNavigation.defaultProps = {
  isLoading: false,
  showViewToggle: false,
  currentView: "week",
  onViewChange: () => {},
}

export default React.memo(CalendarNavigation)
